<script setup lang="ts">
import type { Status } from '@/types';
import { differenceInCalendarDays, isSameYear, lightFormat, parseJSON } from 'date-fns';
import DOMPurify from 'dompurify';
import { computed } from 'vue';

type Item = {
  ui_time: string;
  created_at: string;
  sensitive: boolean;
  spoiler_text: string;
  uri: string;
  url: string;
  content: string;
  account: {
    acct: string;
    display_name: string;
    url: string;
    avatar_static: string;
  };
  media_attachments: unknown[];
  reblog: boolean;
}

const props = defineProps<{
  statuses: Status[]
}>();

const formatForStatus = (date: Date, now: Date) => {
  const diffDays = differenceInCalendarDays(date, now);

  let token: "other" | "thisYear" | "yesterday" | "today";
  if (diffDays === 0) {
    token = "today";
  } else if (diffDays === -1) {
    token = "yesterday";
  } else if (isSameYear(now, date)) {
    token = "thisYear";
  } else {
    token = "other";
  }

  const map = {
    other: 'yy/MM/dd HH:mm:ss',
    thisYear: 'MM/dd HH:mm:ss',
    yesterday: "'昨日' HH:mm:ss",
    today: 'HH:mm:ss',
  }

  const formatStr = map[token];
  return lightFormat(date, formatStr);
}

const items = computed<Item[]>(() => {
  const now = new Date();
  return props.statuses.map(x => {
    const time = formatForStatus(parseJSON(x.created_at), now);
    return {
      ...x,
      content: DOMPurify.sanitize(x.content),
      ui_time: time,
      reblog: x.reblog != null,
    };
  });
});
</script>

<template>
  <p v-if="items.length === 0" class="toast">トゥートはありませんでした。</p>
  <ol v-else class="status-list">
    <li v-for="item in items" :key="item.uri" v-memo="[]">
      <div class="tile status">
        <div class="tile-icon">
          <a :href="item.account.url">
            <img alt="" :src="item.account.avatar_static">
          </a>
        </div>
        <div class="tile-content">
          <div class="tile-title status-head">
            <a :href="item.url"><time :datetime="item.created_at">{{ item.ui_time }}</time></a>
            {{ '@' + item.account.acct }} {{ item.account.display_name }}
          </div>
          <p v-if="item.reblog">
            [Reblogged something]
          </p>
          <div class="status-content-container">
            <p v-if="item.spoiler_text" class="status-content">
              <a :href="item.url" target="_blank">[CW]</a>
              <span v-text="item.spoiler_text"></span>
            </p>
            <div v-else v-html="item.content"></div>
          </div>
          <p v-if="item.media_attachments.length != 0">[メディアあり<span v-if="item.sensitive">(NSFW)</span>]</p>
        </div>
      </div>
    </li>
  </ol>
</template>
